import {
  ChakraBox,
  ChakraFlex,
  ChakraGrid,
  ChakraText,
  DateFormat,
  FAIconName,
  FireflyIconButton,
  Icon,
  IconSize,
  MultiSelectOption,
  Popover,
  SkeletonLoader,
  Text,
  Tooltip,
  UniversalSelect,
} from '@equip.health/ui';
import {
  NOT_ASSESSED_FREQUENCY_CODES,
  NOT_YET_ASSESSED_FREQUENCY_CODES,
} from '~/lib/constants/carePlan.constants';
import { useState } from 'react';
import { getRelativeDateString } from '~/components/maud/providerAssignment/utils';
import { CarePlanFrequencyHistoryResponse } from '~/lib/types/carePlan/carePlans';
import { DateTime } from 'luxon';
import { ProviderAvatarBlockApiWrapper } from '~/components/common/ProviderAvatarBlockApiWrapper';

export type PatientNoteFrequencyRowProps = {
  id: string;
  isButtonDisabled: boolean;
  isChanged: boolean;
  isLoading: boolean;
  isRequired?: boolean;
  label: string;
  onChange: (newFrequency: MultiSelectOption) => void;
  onConfirm: () => void;
  onMenuOpen?: () => void;
  options: MultiSelectOption[];
  placeholder?: string;
  showError?: boolean;
  value: MultiSelectOption | undefined;
  frequencyHistories: CarePlanFrequencyHistoryResponse[];
  isReadyForSigning: boolean;
};

export const PatientNoteFrequencyRow = ({
  id,
  isButtonDisabled,
  isChanged,
  isLoading,
  isRequired = false,
  label,
  onChange,
  onConfirm,
  onMenuOpen,
  options,
  placeholder,
  showError = false,
  value,
  frequencyHistories,
  isReadyForSigning,
}: PatientNoteFrequencyRowProps) => {
  const [showLastAssessedPopover, setShowLastAssessedPopover] =
    useState<boolean>(false);

  // TODO make this possible in the Equip UI component itself, since this is quite brittle
  const dropdownPickTextColor = {
    '& > div > div > div[class*="css-"]': {
      color: !isChanged ? '#A0AEC0 !important' : 'inherit',
    },
  };

  if (isLoading) {
    return <SkeletonLoader id={`patient-note-frequency-row-${id}`} />;
  }

  const latestHistory = frequencyHistories[0];

  const timeStamp = latestHistory
    ? DateTime.fromISO(latestHistory.effectiveDate).toFormat(
        DateFormat.longDayMonthDateWithTime,
      )
    : '';

  return (
    <ChakraGrid
      alignItems="center"
      columnGap="12px"
      templateColumns="1fr auto auto"
      templateRows="1fr"
      width="100%"
      data-is-ready={String(isReadyForSigning)}
    >
      <ChakraFlex alignItems="center">
        {!isChanged &&
          latestHistory &&
          value?.value &&
          !NOT_YET_ASSESSED_FREQUENCY_CODES.includes(value.value) && (
            <Popover
              id={`patient-note-frequency-row-last-assessed-popover-${id}`}
              isOpen={showLastAssessedPopover}
              bodyContent={
                <ChakraFlex direction="column" gap="16px">
                  <ChakraText>
                    Last assessed{' '}
                    {getRelativeDateString(latestHistory.effectiveDate)}
                  </ChakraText>
                  {latestHistory.createdByUserId && (
                    <ProviderAvatarBlockApiWrapper
                      id={latestHistory.createdByUserId}
                      variant="standard"
                      shouldShowTimeStamp
                      timeStamp={timeStamp}
                    />
                  )}
                </ChakraFlex>
              }
              triggerContent={
                <ChakraBox
                  paddingBottom="3px"
                  marginRight="5px"
                  onMouseEnter={() => setShowLastAssessedPopover(true)}
                  onMouseLeave={() => setShowLastAssessedPopover(false)}
                >
                  <Icon
                    aria-label="Last assessed info"
                    name={FAIconName.calendarClockRegular}
                    color="neutral.secondary"
                  />
                </ChakraBox>
              }
            />
          )}
        <Text>{label}</Text>
        {isRequired && (
          <ChakraText color="red.500" marginLeft="4px">
            *
          </ChakraText>
        )}
      </ChakraFlex>
      <ChakraBox width="180px" sx={dropdownPickTextColor}>
        <UniversalSelect
          ariaLabel="Select frequency"
          formatOptionLabel={formatFrequencyOptionLabel}
          id={`patient-note-select-frequency-${id}`}
          isClearable={false}
          isInvalid={showError}
          isSingleSelect
          onChange={onChange}
          onMenuOpen={onMenuOpen}
          options={options}
          placeholder={placeholder}
          value={value}
          variant="small"
        />
      </ChakraBox>
      {!isChanged && (
        <Tooltip
          id={`patient-note-frequency-checkbox-tooltip-${id}`}
          placement="top"
          tooltipText={
            isButtonDisabled ? 'Please select an option from the dropdown' : ''
          }
          isDisabled={!isButtonDisabled}
          width="fit-content"
        >
          <FireflyIconButton
            ariaLabel="Confirm frequency"
            id={`patient-note-frequency-checkbox-${id}`}
            color="teal.400"
            icon={FAIconName.circleCheckRegular}
            variant="tertiary"
            onClick={onConfirm}
            isDisabled={isButtonDisabled}
          />
        </Tooltip>
      )}
    </ChakraGrid>
  );
};

const formatFrequencyOptionLabel = (
  option: MultiSelectOption,
  { context }: { context: 'menu' | 'value' },
) => {
  const isNotAssessed = NOT_ASSESSED_FREQUENCY_CODES.includes(option.value);
  if (!isNotAssessed) {
    return <>{option.label}</>;
  }
  if (context === 'value') {
    return (
      <ChakraFlex alignItems="center" gap="6px">
        <Icon
          color="neutral.secondary"
          name={FAIconName.eyeSlashRegular}
          size={IconSize.small}
        />
        <Text>{option.label}</Text>
      </ChakraFlex>
    );
  }
  return (
    <ChakraBox>
      <ChakraBox
        backgroundColor="neutral.line"
        height="1px"
        marginBottom="8px"
        marginTop="-4px"
        marginX="-12px"
      />
      <ChakraFlex alignItems="center" gap="8px">
        <Icon
          color="neutral.secondary"
          name={FAIconName.eyeSlashRegular}
          size={IconSize.medium}
        />
        <Text>{option.label}</Text>
      </ChakraFlex>
    </ChakraBox>
  );
};
